import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '/Foundation/SitecoreGraphQL/code/clientv2.js';
import { RootState } from '../Store/store.ts';
import { getPerCategorySearchString, getSearchString } from '../Util/SearchString.ts';
import { getWindowData } from '../../FitAssistantShared/GetWindowData.js';
import { GET_OPTIONS_QUERY, GET_PRODUCT_QUERY } from '../Queries/Queries.ts';
import { getAdditionalFilterForOptions } from '../../FitAssistantShared/Functions.js';
import { GetOptionsQuery, GetProductsQuery, QueryVariables } from '../Types/FitAssistantGraphQLTypes.ts';
import { StepData } from '../Types/FitAssistantData.ts';
import { RejectValue } from './Types.ts';
import { ProductLoader } from '../Helpers/ProductLoader.ts';
import { IsProductPage } from '../../FitAssistantShared/IsProductPage.js';
import { ProductSearchType } from '../Enums/ProductSearchType.ts';
import { ProductPageHelper } from '../Helpers/ProductPageHelper.ts';

export const getOptions = createAsyncThunk<StepData, void, RejectValue>(
  'fitAssistant/getOptions',
  async (_, { getState, rejectWithValue }) => {
    let state = getState() as RootState;
    let carInformation = state.FitAssistant.carInformation;
    const searchString = `${getSearchString(carInformation)},${getPerCategorySearchString(carInformation)}`;
    const currentStepData = state.FitAssistant.initialData.Steps.find((x) => x.Parameter === state.FitAssistant.step);

    const getOptionsVariables: QueryVariables = {
      siteName: getWindowData().SitecoreSiteName,
      language: getWindowData().SitecoreLanguage,
      configurationItem: currentStepData?.Id,
      parameters: searchString,
      additionalFilter: getAdditionalFilterForOptions(
        currentStepData,
        state.FitAssistant.initialData.AdditionalFilters
      ),
      ispreview: state.FitAssistant.initialData.IsPreview,
    };

    let result = await client.query<GetOptionsQuery>(GET_OPTIONS_QUERY, getOptionsVariables);
    if (result.error) return rejectWithValue(result.error);

    let stepToAdd: StepData = {
      data: result.data.getCarSelectorOptions.carSelectorOptionsResponse.Options,
      messages: result.data.getCarSelectorOptions.carSelectorOptionsResponse.OptionMessages,
      parameters: result.data.getCarSelectorOptions.carSelectorOptionsResponse.ParameterName,
      configItems: result.data.getCarSelectorOptions.carSelectorOptionsResponse.NextConfigurationItem,
    };

    return stepToAdd;
  }
);

export const getProducts = createAsyncThunk<GetProductsQuery, ProductSearchType, RejectValue>(
  'fitAssistant/getProducts',
  async (productSearchType, { getState, dispatch, rejectWithValue }) => {
    let state = getState() as RootState;

    let carInformation = state.FitAssistant.carInformation;
    let currentStepContent = state.FitAssistant.initialData.Steps.find((x) => x.Parameter === state.FitAssistant.step);
    let currentStepData = state.FitAssistant.stepData.find((x) => x.parameters === state.FitAssistant.step);
    let parameters = `${getSearchString(carInformation)},${getPerCategorySearchString(carInformation)}`;

    const getOptionsVariables: QueryVariables = {
      siteName: getWindowData().SitecoreSiteName,
      language: getWindowData().SitecoreLanguage,
      configurationItem: currentStepContent?.Id,
      selectedproductpimid: state.ProductDetail.ProductId || '',
      parameters: parameters,
      additionalFilter: getAdditionalFilterForOptions(
        currentStepContent,
        state.FitAssistant.initialData.AdditionalFilters
      ),
      ispreview: state.FitAssistant.initialData.IsPreview,
    };

    let result = await client.query<GetProductsQuery>(GET_PRODUCT_QUERY, getOptionsVariables);

    if (result.error) return rejectWithValue(result.error);

    let productLoader = new ProductLoader(
      result.data,
      state.FitAssistant.carInformation,
      null,
      true,
      state.FitAssistant.initialData,
      state.FitAssistant.stepData,
      true,
      dispatch
    );

    switch (productSearchType) {
      case ProductSearchType.SelectList:
        productLoader.HandleSelectListClick();
        break;
      case ProductSearchType.VariationSelector:
        productLoader.Load();
        break;
      default:
        productLoader.Load();

        if (IsProductPage) {
          ProductPageHelper.HandleProductPageData(
            currentStepData!,
            state.ProductDetail,
            state.FitAssistant.initialData,
            carInformation,
            dispatch
          );
        }
    }

    return result.data;
  }
);
